import { useState, useEffect } from 'react';
import {
  Smartphone,
  Power,
  PowerOff,
  Loader2,
  AlertCircle,
  Check,
  Settings,
  Shield,
} from 'lucide-react';
import api from '../../services/api';
import { useTenantStore } from '../../stores/tenantStore';
import ConfirmDialog from '../common/ConfirmDialog';
// Device groups now managed via Device Groups page
import IKEv2CertificateManager from './IKEv2CertificateManager';

interface AppServerConfig {
  id: string;
  enabled: boolean;
  listenPort: number;
  serverAddress: string;
  serverPublicKey: string;
  endpoint: string | null;
  sourceInterface: string | null;
  status: string;
  connectedPeers: number;
  tunnelMode: 'split' | 'full';
  internetBreakout: boolean;
  allowedIps: string[];
  defaultDeviceGroupId: string | null;
  tunnelProtocol: string;
  edgeGuid: string | null;
}

interface LogicalInterface {
  id: string;
  name: string;
  ipv4: string | null;
  subnet: string | null;
  dhcp_client: boolean;
  operationalState: string;
}

interface WireGuardAppServerConfigProps {
  edgeId: string;
  onConfigChange?: () => void;
}

const WireGuardAppServerConfig = ({ edgeId, onConfigChange }: WireGuardAppServerConfigProps) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [configured, setConfigured] = useState(false);
  const [config, setConfig] = useState<AppServerConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [logicalInterfaces, setLogicalInterfaces] = useState<LogicalInterface[]>([]);
  const [loadingInterfaces, setLoadingInterfaces] = useState(false);
  // Device groups now managed via Device Groups page — state removed

  // Form State
  const [selectedInterface, setSelectedInterface] = useState('');
  // Default to IKEv2 port 500; was 51821 (WG) before the IKEv2-only UI switch.
  const [listenPort, setListenPort] = useState(500);
  const [tunnelMode, setTunnelMode] = useState<'split' | 'full'>('split');
  const [internetBreakout, setInternetBreakout] = useState(false);
  const [allowedIps, setAllowedIps] = useState<string>(''); // Comma separated string for UI
  const [defaultDeviceGroupId, setDefaultDeviceGroupId] = useState<string>('');
  // Current release: IKEv2-only. WireGuard support is retained in the API and
  // agent for the next release; the UI selector is hidden so admins can't pick
  // WG. Switch back to 'wireguard' default + un-hide the selector below when
  // re-enabling.
  const [tunnelProtocol, setTunnelProtocol] = useState<string>('ikev2');
  const [dnsServers, setDnsServers] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  // Confirm dialog state
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const fetchLogicalInterfaces = async () => {
    setLoadingInterfaces(true);
    try {
      // Don't include /api/v1 in URL - baseURL already has it
      const response = await api.get(`/edges/${edgeId}/interfaces/logical`);
      const interfaces = response.data.items || [];
      // Filter to only show interfaces with an IP address (DHCP or static)
      const staticInterfaces = interfaces.filter((iface: LogicalInterface) =>
        iface.ipv4
      );
      setLogicalInterfaces(staticInterfaces);
    } catch (err: any) {
      console.error('Failed to load logical interfaces:', err);
    } finally {
      setLoadingInterfaces(false);
    }
  };

  // fetchDeviceGroups removed — DGs now managed via Device Groups page

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v2/wireguard/app-server/${edgeId}`);
      const data = response.data;

      // Transform snake_case API response to camelCase for UI
      const config: AppServerConfig = {
        id: data.id,
        enabled: data.enabled === 1 || data.enabled === true,
        listenPort: data.listen_port,
        serverAddress: data.server_address,
        serverPublicKey: data.server_public_key,
        endpoint: data.endpoint_host ? `${data.endpoint_host}:${data.listen_port}` : null,
        sourceInterface: data.source_interface,
        status: data.status,
        connectedPeers: data.connected_peers || 0,
        tunnelMode: data.tunnel_mode || 'split',
        internetBreakout: data.internet_breakout === 1,
        allowedIps: Array.isArray(data.allowed_ips) ? data.allowed_ips : (typeof data.allowed_ips === 'string' ? JSON.parse(data.allowed_ips) : []),
        defaultDeviceGroupId: data.default_device_group_id || null,
        tunnelProtocol: data.tunnel_protocol || 'wireguard',
        edgeGuid: data.edge_guid || data.dev_ser_num || null,
      };

      setConfigured(true);
      setConfig(config);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setConfigured(false);
        setConfig(null);
      } else {
        setError(err.response?.data?.message || 'Failed to load configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // fetchDeviceGroups removed // Load groups for display
  }, [edgeId, selectedTenantId]);

  const handleEnableClick = () => {
    setIsEditing(false);
    setSelectedInterface('');
    setListenPort(51821);
    setTunnelMode('split');
    setAllowedIps('');
    setDefaultDeviceGroupId('');
    setTunnelProtocol('ikev2');
    setDnsServers('');
    fetchLogicalInterfaces();
    // fetchDeviceGroups removed
    setShowConfigModal(true);
  };

  const handleEditClick = () => {
    if (!config) return;
    setIsEditing(true);
    setSelectedInterface(config.sourceInterface || '');
    setListenPort(config.listenPort);
    setTunnelMode(config.tunnelMode);
    setInternetBreakout(config.internetBreakout);
    setAllowedIps(config.allowedIps.join(', '));
    setDefaultDeviceGroupId(config.defaultDeviceGroupId || '');
    setTunnelProtocol(config.tunnelProtocol || 'wireguard');
    setDnsServers('');
    fetchLogicalInterfaces();
    // fetchDeviceGroups removed
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedInterface && !isEditing) { // Interface required for enable, optional for update (if not changing)
      setError('Please select a source interface');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: any = {
        listenPort: tunnelProtocol === 'ikev2' ? 500 : listenPort,
        tunnelMode,
        internetBreakout,
        tunnelProtocol,
        allowedIps: tunnelMode === 'split'
          ? allowedIps.split(',').map(ip => ip.trim()).filter(ip => ip.length > 0)
          : undefined,
        defaultDeviceGroupId: defaultDeviceGroupId || undefined,
      };

      if (tunnelProtocol === 'ikev2' && dnsServers.trim()) {
        payload.dnsServers = dnsServers.split(',').map(s => s.trim()).filter(s => s.length > 0);
      }

      if (selectedInterface) {
        payload.sourceInterface = selectedInterface;
      }

      if (isEditing) {
        await api.patch(`/api/v2/wireguard/app-server/${edgeId}`, payload);
        setSuccess('Configuration updated successfully');
      } else {
        payload.edgeId = edgeId;
        await api.post('/api/v2/wireguard/app-server/enable', payload);
        setSuccess(`${tunnelProtocol === 'ikev2' ? 'IKEv2' : 'WireGuard'} app server enabled successfully`);
      }

      setShowConfigModal(false);
      setTimeout(() => {
        fetchConfig();
        onConfigChange?.();
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'enable'} app server`);
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = () => {
    setShowDisableConfirm(true);
  };

  const confirmDisable = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(`/api/v2/wireguard/app-server/${edgeId}/disable`);
      setSuccess('App server disabled successfully');
      setShowDisableConfirm(false);
      setTimeout(() => {
        fetchConfig();
        onConfigChange?.();
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable app server');
    } finally {
      setSaving(false);
    }
  };

  const isIKEv2 = config?.tunnelProtocol === 'ikev2';
  const protocolLabel = isIKEv2 ? 'IKEv2' : 'WireGuard';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading app server configuration...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          {isIKEv2 ? (
            <Shield className="w-6 h-6 text-blue-600" />
          ) : (
            <Smartphone className="w-6 h-6 text-purple-600" />
          )}
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isIKEv2 ? 'IKEv2 App Server' : 'WireGuard App Server (wg1)'}
            </h2>
            <p className="text-sm text-gray-500">
              {isIKEv2
                ? 'IKEv2 VPN server with certificate authentication for native OS clients'
                : 'VPN server for mobile and desktop application clients'}
            </p>
          </div>
        </div>
        {configured && config && (
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.enabled && config.status === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
              }`}
          >
            {config.enabled && config.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
          <Check className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      {/* Not Configured State */}
      {!configured && (
        <div className="text-center py-8">
          <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            App Server Not Configured
          </h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Enable the WireGuard app server to allow mobile and desktop VPN clients
            to connect to this edge. This uses wg1 interface on VRF 2, port 51821.
          </p>
          <button
            onClick={handleEnableClick}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enabling...
              </>
            ) : (
              <>
                <Power className="w-4 h-4 mr-2" />
                Enable App Server
              </>
            )}
          </button>
        </div>
      )}

      {/* Configured State */}
      {configured && config && (
        <div className="space-y-6">
          {/* Configuration Details */}
          <div className="flex justify-end">
            <button
              onClick={handleEditClick}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center"
            >
              <Settings className="w-4 h-4 mr-1" />
              Edit Configuration
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Protocol
              </label>
              <p className="mt-1 text-sm text-gray-900">{protocolLabel}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Listen Port
              </label>
              <p className="mt-1 text-sm text-gray-900">{config.listenPort}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {isIKEv2 ? 'Virtual IP Pool' : 'Server Address'}
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {isIKEv2 ? '10.11.0.0/16' : config.serverAddress}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Source Interface
              </label>
              <p className="mt-1 text-sm text-gray-900">{config.sourceInterface || 'Not set'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Endpoint
              </label>
              <p className="mt-1 text-sm text-gray-900">{config.endpoint || 'Not available'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Status
              </label>
              <p className="mt-1 text-sm text-gray-900">{config.status}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Connected Peers
              </label>
              <p className="mt-1 text-sm text-gray-900">{config.connectedPeers}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Tunnel Mode
              </label>
              <p className="mt-1 text-sm text-gray-900 capitalize">{config.tunnelMode}</p>
            </div>
            {config.tunnelMode === 'full' && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Internet Breakout
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {config.internetBreakout ? 'Direct (uninspected)' : 'Via dataplane'}
                </p>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Device Groups
              </label>
              <p className="mt-1 text-sm text-gray-500 italic">Managed via Device Groups page</p>
            </div>
            {config.tunnelMode === 'split' && (
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Allowed IPs (Split Tunnel)
                </label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {config.allowedIps && config.allowedIps.length > 0 ? (
                    config.allowedIps.map((ip, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {ip}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500 italic">Default (VPN Network Only)</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Server Public Key - only show for WireGuard */}
          {!isIKEv2 && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Server Public Key
              </label>
              <div className="mt-1 flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-900 overflow-x-auto">
                  {config.serverPublicKey}
                </code>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
            {config.enabled ? (
              <button
                onClick={handleDisable}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Disabling...
                  </>
                ) : (
                  <>
                    <PowerOff className="w-4 h-4 mr-2" />
                    Disable Server
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleEnableClick}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              >
                <Power className="w-4 h-4 mr-2" />
                Enable Server
              </button>
            )}
          </div>
        </div>
      )}

      {/* IKEv2 Certificate Manager */}
      {isIKEv2 && configured && config?.edgeGuid && (
        <IKEv2CertificateManager edgeId={edgeId} edgeGuid={config.edgeGuid} />
      )}

      {/* Edit/Enable Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <Settings className="w-6 h-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditing ? 'Edit App Server Configuration' : 'Enable App Server'}
                </h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {isEditing
                  ? 'Update the app server configuration. Clients may need to reconnect.'
                  : 'Select the physical or logical interface that will serve as the source for the app server. This interface must have a static IP address.'
                }
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Tunnel Protocol — IKEv2-only for the current release.
                    WireGuard support is retained server-side and may return
                    next release; restore the original <select> block (see git
                    history for this file) to re-expose the selector. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tunnel Protocol
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                    IKEv2 (Certificate Auth)
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Native IKEv2 with X.509 certificates. Works with iOS, macOS, Android, and Windows built-in VPN.
                  </p>
                </div>

                {/* Source Interface */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source Interface *
                  </label>
                  {loadingInterfaces ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Loading interfaces...
                    </div>
                  ) : (
                    <select
                      value={selectedInterface}
                      onChange={(e) => setSelectedInterface(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                    >
                      <option value="">Select an interface</option>
                      {logicalInterfaces.map((iface) => (
                        <option key={iface.id} value={iface.name}>
                          {iface.name} - {iface.ipv4}/{iface.subnet}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Only interfaces with static IP addresses are shown
                  </p>
                </div>

                {/* Listen Port - hidden for IKEv2 (auto 500) */}
                {tunnelProtocol !== 'ikev2' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Listen Port
                    </label>
                    <input
                      type="number"
                      value={listenPort}
                      onChange={(e) => setListenPort(parseInt(e.target.value) || 51821)}
                      min={1024}
                      max={65535}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Default: 51821 (UDP)
                    </p>
                  </div>
                )}

                {/* IKEv2-specific: port info and server address info */}
                {tunnelProtocol === 'ikev2' && (
                  <>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        IKEv2 uses UDP ports 500 and 4500 (NAT-T). The virtual IP pool is 10.11.0.0/16 with server address 10.11.0.254.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        DNS Servers (optional)
                      </label>
                      <input
                        type="text"
                        value={dnsServers}
                        onChange={(e) => setDnsServers(e.target.value)}
                        placeholder="e.g. 1.1.1.1, 8.8.8.8"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Comma-separated DNS servers pushed to IKEv2 clients. Leave empty for defaults.
                      </p>
                    </div>
                  </>
                )}

                {/* Tunnel Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tunnel Mode
                  </label>
                  <select
                    value={tunnelMode}
                    onChange={(e) => setTunnelMode(e.target.value as 'split' | 'full')}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                  >
                    <option value="split">Split Tunnel (Recommended)</option>
                    <option value="full">Full Tunnel (All Traffic)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {tunnelMode === 'split'
                      ? 'Only traffic to specific networks goes through VPN'
                      : 'All device traffic routes through VPN (internet access via edge)'}
                  </p>
                </div>

                {/* Internet Breakout — full tunnel only. In split tunnel the client
                    sends only tenant-destined traffic into the tunnel, so the edge never
                    sees internet-bound client traffic and this setting does nothing. */}
                {tunnelMode === 'full' && (
                  <div>
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={internetBreakout}
                        onChange={(e) => setInternetBreakout(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Direct Internet Breakout
                      </span>
                    </label>
                    <p className="mt-1 ml-6 text-xs text-gray-500">
                      Send internet-bound client traffic straight out the edge WAN instead of
                      through the dataplane. Traffic to your own networks is unaffected.
                    </p>
                    {internetBreakout && (
                      <p className="mt-1 ml-6 text-xs text-amber-700">
                        Internet traffic will bypass ACLs, routing policies, Suricata IDS/IPS
                        and flow records. Traffic to your own networks remains inspected.
                      </p>
                    )}
                  </div>
                )}

                {tunnelMode === 'split' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allowed IPs (Optional)
                    </label>
                    <input
                      type="text"
                      value={allowedIps}
                      onChange={(e) => setAllowedIps(e.target.value)}
                      placeholder="e.g. 10.0.0.0/8, 192.168.1.0/24"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Comma-separated CIDR ranges. Leave empty to route only VPN traffic.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Groups
                  </label>
                  <p className="text-sm text-gray-500 italic">
                    Device groups are now managed via the Device Groups page with edge binding.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowConfigModal(false);
                    setError(null);
                    if (!isEditing) setSelectedInterface('');
                  }}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConfig}
                  disabled={saving || (!selectedInterface && !isEditing)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {isEditing ? 'Save Configuration' : 'Enable Server'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Disable Dialog */}
      <ConfirmDialog
        isOpen={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onConfirm={confirmDisable}
        title="Disable App Server"
        message={`Are you sure you want to disable the ${protocolLabel} app server? All connected clients will be disconnected.`}
        confirmText="Disable"
        variant="warning"
        isLoading={saving}
      />
    </div>
  );
};

export default WireGuardAppServerConfig;
